/**
 * getOverheadForPeriod.js
 *
 * Calculates the total overhead cost for a given period, supporting
 * multiple overhead rows (each with its own date_from / date_to).
 *
 * For each row that overlaps the selected period, the overhead contribution
 * is prorated: monthly_total × exact calendar months of overlap.
 *
 * Returns a single overhead object whose `annual_total` represents the
 * total overhead applicable to the selected period (used by calculateMargins).
 */

// Count exact calendar months in [start, end] (same logic as calculateMargins.js)
function calcMonths(start, end) {
  if (start > end) return 0;
  let total = 0;
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end) {
    const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const daysInMonth = monthEnd.getDate();
    const s = start > cur ? start : cur;
    const e = end < monthEnd ? end : monthEnd;
    total += ((e - s) / 86400000 + 1) / daysInMonth;
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return total;
}

export function getOverheadForPeriod(overhead, periodFrom, periodTo) {
  // Support both legacy single object and new array format
  const rows = Array.isArray(overhead)
    ? overhead
    : overhead
    ? [overhead]
    : [];

  if (!rows.length) return null;

  const from = new Date(periodFrom);
  // Charge the full, known monthly overhead for the SELECTED period, independent
  // of the wall-clock (finding #24). The previous code capped `to` at today, so
  // a fully-future period (e.g. Q3 2026 selected on 2026-06-09) yielded to < from,
  // no overhead row overlapped, the function returned null, and margin was
  // overstated. Reports are now reproducible regardless of when they are run.
  const to = new Date(periodTo);

  let totalForPeriod = 0;
  // Headcount is taken from the overlapping row with the latest date_from
  // (mirrors the "latest date_from wins" rule used in calculateMargins.js /
  // calculateConsultantClientTable.js), not from whichever row happens to be
  // last in the source array.
  let latestConsultants = 1;
  let latestFrom = -Infinity;
  let hasOverlap = false;

  rows.forEach((row) => {
    if (!row.monthly_total) return;

    const rFrom = new Date(row.date_from);
    const rTo = new Date(row.date_to);

    // Skip rows that don't overlap the selected period
    if (rFrom > to || rTo < from) return;

    const overlapFrom = from > rFrom ? from : rFrom;
    const overlapTo = to < rTo ? to : rTo;
    const months = calcMonths(overlapFrom, overlapTo);

    totalForPeriod += row.monthly_total * months;

    const rFromMs = rFrom.getTime();
    if (rFromMs >= latestFrom) {
      latestFrom = rFromMs;
      latestConsultants = row.consultants || 1;
    }
    hasOverlap = true;
  });

  if (!hasOverlap) return null;

  return {
    // "annual_total" here = total overhead for the selected period
    annual_total: totalForPeriod,
    monthly_total: totalForPeriod,
    consultants: latestConsultants,
    monthly_per_consultant: latestConsultants > 0 ? totalForPeriod / latestConsultants : 0,
  };
}
